// ============================================
// CREW AT SEA - USER PANEL (SEAFARER)
// ============================================

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Toaster } from 'react-hot-toast';
import { routeTree } from './routeTree.gen';
import { initializeApiClient } from './services/api';
import { useAuthStore } from './store/auth';
import { createMutationErrorHandler, createQueryErrorHandler } from './lib/error-handler';
import { initI18n } from '@crew-at-sea/i18n';
import './index.css';

// i18n başlat
initI18n('user');

// Initialize API client
initializeApiClient();

// Get auth actions for error handler
const getAuthActions = () => {
  const { logout } = useAuthStore.getState();
  return {
    logout,
    clearQueryCache: () => queryClient.clear(),
  };
};

// Create query client with global error handlers
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,  // 2 dakika
      gcTime: 1000 * 60 * 5,     // 5 dakika
      retry: (failureCount, error: any) => {
        // 401, 403, 404 hatalarında retry yapma
        const status = error?.response?.status || error?.status;
        if ([401, 403, 404].includes(status)) return false;
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false, // Mutation'larda retry yapma
    },
  },
  mutationCache: new MutationCache({
    onError: (error) => {
      const { logout, clearQueryCache } = getAuthActions();
      const handler = createMutationErrorHandler(logout, clearQueryCache);
      handler(error);
    },
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      const { logout, clearQueryCache } = getAuthActions();
      const handler = createQueryErrorHandler(logout, clearQueryCache);
      handler(error);
    },
  }),
});

// Create router
const router = createRouter({
  routeTree,
  context: {
    queryClient,
    auth: undefined!,
  },
  defaultPreload: 'intent',
});

// Type registration
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// App component with auth context
function App() {
  const auth = useAuthStore();
  
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ queryClient, auth }} />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            style: {
              background: '#059669',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#dc2626',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

// Render
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
